import { useEffect, useState, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";

import useAxios from "../../../utils/useAxios";
import { resObj, paginationVal } from "../../../utils/constants";
import PermissionContext from "../../../context/PermissionContext";
import DrawerContext from "../../../context/DrawerContext";
import AutoLogout from "../../../utils/useAutoLogout";

import Pagination from "@mui/material/Pagination";

import SideDrawer from "../../../components/layout/SideDrawer";
import ListHeader from "../../../components/layout/ListHeader";
import CustomTable from "../../../components/layout/CustomTable";
import NoData from "../../../components/layout/NoData";
import SuccessAlert from "../../../components/layout/SuccessAlert";
import ErrorModal from "../../../components/layout/ErrorModal";
import DeleteModal from "../../../components/layout/DeleteModal";
import Loader from "../../../components/layout/Loader";

const LipidRawMaterialListPage = () => {
  const api = useAxios();
  const navigate = useNavigate();

  const { permisionsList, checkUserPermission } = useContext(PermissionContext);
  const { drawerValue, setDrawerValue } = useContext(DrawerContext);

  useEffect(() => {
    if (permisionsList.length > 0) checkUserPermission(['Materials Capture-Lipid Raw Materials']);
  }, [permisionsList]);

  const [apiRes, setApiRes] = useState(resObj);
  const [lipidRawMaterialsList, setLipidRawMaterialsList] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [lipidId, setLipidId] = useState('');

  const pageData = useRef({ ...paginationVal });

  const tableHeading = ['SI No', 'Lipid Type', 'Lipid Name', 'Supplier', 'Product Code', 'Aliquot Count'];

  const deleteModalTitle = 'Lipid Raw Material Delete';
  const deleteModalMessage = `Are you sure you want to delete? 
    (All related informations will be lost)`;

  useEffect(() => {
    getLipidRawMaterials();
  }, []);

  const handlePaginationClick = (e, pageNumber) => {
    pageData.current.pageNumber = pageNumber;
    getLipidRawMaterials();
  };

  const navigateToLipidCreate = () => {
    navigate('/lipid-raw-materials/create');
  };

  const navigateToLipidEdit = id => {
    navigate(`/lipid-raw-materials/edit/${id}`);
  };

  const getLipidRawMaterials = async () => {
    try {
      setApiRes({ ...apiRes, loading: true });
      const response = await api.get(
        '/api/materials-capture/lipid-raw-materials/list/get', {
        params: {
          search_term: pageData.current.searchTerm,
          page_number: +pageData.current.pageNumber,
        }
      });

      if (response.status === 200) {
        setApiRes({ ...apiRes, loading: false });
        setLipidRawMaterialsList(response.data.data);
        pageData.current.pageCount = response.data.page_count;
      }
    } catch (err) {
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Lipid Raw Material Get',
        loading: false,
      });
    }
  };

  const deleteLipidRawMaterial = async () => {
    try {
      setApiRes({ ...apiRes, loading: true });
      setDeleteModalOpen(false);
      const response = await api.delete(
        `/api/materials-capture/lipid-raw-materials/delete/${lipidId}`
      );

      if (response.status === 200) {
        getLipidRawMaterials();
        setApiRes({
          ...apiRes,
          showAlert: true,
          successMsg: 'Lipid Raw Material deleted successfully',
          loading: false,
        });
      }
    } catch (err) {
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Lipid Raw Material Delete',
        loading: false,
      });
    }
  };

  return (
    <AutoLogout>
      <SideDrawer drawerValue={drawerValue} setDrawerValue={setDrawerValue} />
      {apiRes.loading && <Loader />}
      <div className="mainContainer" style={{ marginLeft: drawerValue.drawerWidth }}>
        {apiRes.showAlert && <SuccessAlert apiRes={apiRes} setApiRes={setApiRes} />}
        <ListHeader
          title="Lipid Raw Materials"
          handleClick={navigateToLipidCreate}
          search={getLipidRawMaterials}
          pageData={pageData}
          hasCreatePermission={permisionsList.includes('Materials Capture-Lipid Raw Materials')}
        />

        <CustomTable
          heading={tableHeading}
          rows={lipidRawMaterialsList}
          navigateToEdit={navigateToLipidEdit}
          setDeleteModalOpen={setDeleteModalOpen}
          setId={setLipidId}
          pageData={pageData}
          idKey={'id'}
          isDeleteBtnVisible={permisionsList.includes('Admin-Manage Role')}
          showToolTip={false}
        />

        {lipidRawMaterialsList.length === 0 && <NoData />}

        {(lipidRawMaterialsList.length !== 0 && pageData.current.pageCount !== 1) && <Pagination
          style={{ marginTop: '10px', float: 'right' }}
          count={pageData.current.pageCount}
          onChange={handlePaginationClick} />}

        <ErrorModal apiRes={apiRes} setApiRes={setApiRes} />

        <DeleteModal
          title={deleteModalTitle}
          message={deleteModalMessage}
          open={deleteModalOpen}
          setOpen={setDeleteModalOpen}
          onDelete={deleteLipidRawMaterial}
        />
      </div>
    </AutoLogout>
  );
};

export default LipidRawMaterialListPage;